// frontend/src/pages/visitor/Events.tsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, Clock, MapPin } from 'react-feather';
import VisitorLayout from '../../components/layouts/VisitorLayout';
import Loader from '../../components/common/Loader';
import CoupleService from '../../services/couple.service';
import EventService from '../../services/event.service';
import { Couple, Event } from '../../types';

const Events: React.FC = () => {
  const { customUrl } = useParams<{ customUrl: string }>();
  const [couple, setCouple] = useState<Couple | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!customUrl) return;

      try {
        setLoading(true);
        
        // Fetch couple by custom URL
        const coupleData = await CoupleService.getCoupleByCustomUrl(customUrl);
        setCouple(coupleData);
        
        if (coupleData) {
          // Fetch events
          const eventsData = await EventService.getCoupleEvents(coupleData._id);
          
          // Sort events by date (earliest first)
          const sortedEvents = eventsData.sort((a, b) => 
            new Date(a.date).getTime() - new Date(b.date).getTime()
          );
          
          setEvents(sortedEvents);
        }
        
        setError(null);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load events';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [customUrl]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  // Group events by date
  const groupEventsByDate = () => {
    const groups: Record<string, Event[]> = {};
    
    events.forEach(event => {
      const date = new Date(event.date).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(event);
    });
    
    return Object.entries(groups).map(([date, events]) => ({
      date: new Date(date),
      events
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader size="lg" label="Loading events..." />
      </div>
    );
  }

  if (error || !couple) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Calendar className="mx-auto h-12 w-12 text-pink-500" />
          <h2 className="mt-2 text-lg font-medium text-gray-900">Events Not Found</h2>
          <p className="mt-1 text-sm text-gray-500">
            {error || "We couldn't find the events you're looking for."}
          </p>
        </div>
      </div>
    );
  }

  const eventGroups = groupEventsByDate();

  return (
    <VisitorLayout couple={couple}>
      <div className="bg-white">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">Wedding Events</h2>
            <p className="mt-3 max-w-2xl mx-auto text-lg text-gray-500 sm:mt-4">
              Here's what we have planned for our special day
            </p>
          </div>
          
          {events.length === 0 ? (
            <div className="mt-12 text-center py-10 bg-gray-50 rounded-lg">
              <Calendar className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No events scheduled</h3>
              <p className="mt-1 text-sm text-gray-500">
                Check back later for event details.
              </p>
            </div>
          ) : (
            <div className="mt-12 max-w-3xl mx-auto">
              <div className="flow-root">
                <ul className="-mb-8">
                  {eventGroups.map((group, groupIdx) => (
                    <li key={group.date.toISOString()}>
                      <div className="relative pb-8">
                        {groupIdx !== eventGroups.length - 1 ? (
                          <span
                            className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                            aria-hidden="true"
                          />
                        ) : null}
                        
                        <div className="relative flex space-x-3">
                          <div>
                            <span className="h-8 w-8 rounded-full bg-pink-500 flex items-center justify-center ring-8 ring-white">
                              <Calendar className="h-4 w-4 text-white" aria-hidden="true" />
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {formatDate(group.date.toISOString())}
                              </div>
                            </div>
                            
                            <div className="mt-4 space-y-8">
                              {group.events.map((event) => (
                                <div
                                  key={event._id}
                                  className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
                                >
                                  <h3 className="text-lg font-medium text-gray-900">{event.title}</h3>
                                  
                                  <div className="mt-3 space-y-2">
                                    {event.time && (
                                      <div className="flex items-center text-sm text-gray-500">
                                        <Clock className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                                        <span>{event.time}</span>
                                      </div>
                                    )}
                                    
                                    {event.location && (
                                      <div className="flex items-center text-sm text-gray-500">
                                        <MapPin className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                                        <span>{event.location}</span>
                                      </div>
                                    )}
                                    
                                    {event.address && (
                                      <p className="mt-1 text-sm text-gray-500 pl-6">
                                        {event.address}
                                      </p>
                                    )}
                                  </div>
                                  
                                  {event.description && (
                                    <div className="mt-4 text-sm text-gray-500">
                                      <p className="whitespace-pre-line">{event.description}</p>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </VisitorLayout>
  );
};

export default Events;